/**
 * `?org=`: the scope bar's selection, in the address.
 *
 * The bar narrows lists. Its selection used to live only in the persisted
 * `contextStore`, so a link to a list did not say which organization it was
 * narrowed to, a reload in another tab could show different rows, and Back did
 * not move the selection. Carrying it in the address fixes all three.
 *
 * It narrows lists and nothing else. The request interceptor still reads the
 * store, which this keeps in step with the address, and the server still
 * checks every selection against the caller's own branch. A configuration
 * page that edits one organization takes it from its route, never from here.
 *
 * The value is one word:
 *
 *  - an organization id, for a distributor, an MSP or a company. Every
 *    organization is one row with one id, so the id alone names it; which
 *    level it sits at is read from the org lists the bar already loads.
 *  - `all`, platform staff's whole-platform view.
 *  - `default`, the caller's own default scope.
 *
 * The two words are the two `X-Context-Scope` values that travel without an
 * `X-Context-Id`, so the address uses the header's own vocabulary and a
 * selection reloads into exactly the headers it was sent with. Neither can be
 * mistaken for an id, which is always a UUID. `default` is written rather than
 * left out because an address without the param means "not said", which keeps
 * whatever the store holds; Back to a default view has to say "default" or it
 * would keep the narrowed one.
 */

import { toast } from "sonner";

import { isOrganizationId } from "@/lib/utils";
import {
  useContextStore,
  type ContextScope,
  type ViewContext,
} from "@/store/contextStore";

export const ORG_PARAM = "org";

/** Platform staff's whole-platform view. */
export const ALL_TOKEN = "all";
/** The caller's own default scope: no header, the server answers from who they are. */
export const DEFAULT_TOKEN = "default";

/** The ids the interceptor reads off a selection. */
export interface ScopeSelection {
  scope: ContextScope;
  distributorId: string | null;
  mspId: string | null;
  companyId: string | null;
}

/**
 * A selection as the address spells it.
 *
 * Lower-cased, so the address and the store compare equal whichever way an id
 * was typed. A scope whose id the interceptor would not send reads as the
 * default, because the request it makes is the default's.
 */
export function tokenOf(selection: ScopeSelection): string {
  if (selection.scope === "all") return ALL_TOKEN;
  const id =
    selection.scope === "distributor"
      ? selection.distributorId
      : selection.scope === "msp"
        ? selection.mspId
        : selection.scope === "company"
          ? selection.companyId
          : null;
  return id && isOrganizationId(id) ? id.toLowerCase() : DEFAULT_TOKEN;
}

/** What the address holds, read without judging whether the caller may use it. */
export type OrgParam =
  | { kind: "absent" }
  | { kind: "all" }
  | { kind: "default" }
  | { kind: "organization"; id: string }
  | { kind: "malformed" };

/** A value the address actually holds, which is what can be resolved. */
export type NamedOrgParam = Exclude<OrgParam, { kind: "absent" }>;

export function parseOrgParam(raw: string | null): OrgParam {
  const value = raw?.trim().toLowerCase() ?? "";
  // An empty `?org=` names nothing, so it is read as not said at all.
  if (!value) return { kind: "absent" };
  if (value === ALL_TOKEN) return { kind: "all" };
  if (value === DEFAULT_TOKEN) return { kind: "default" };
  if (isOrganizationId(value)) return { kind: "organization", id: value };
  return { kind: "malformed" };
}

/** The spelling the address should carry for a value, or `null` if it has none. */
export function canonicalOrg(param: OrgParam): string | null {
  if (param.kind === "all") return ALL_TOKEN;
  if (param.kind === "default") return DEFAULT_TOKEN;
  if (param.kind === "organization") return param.id;
  return null;
}

/** Why an address's organization was not used. */
export type FallbackReason =
  | "malformed"
  | "unknown"
  | "role"
  | "unverified"
  | "refused";

/**
 * What the caller may select, as the scope bar knows it.
 *
 * The lists are the caller's own branch (`scopeData.ts` asks for exactly that),
 * so an organization found in them is one the bar could have offered. The
 * allowlists are an account manager's assignment, applied here as the bar
 * applies them, so a link never selects something the bar would hide.
 */
export interface OrgReach {
  isPlatformStaff: boolean;
  allowedDistributorIds: readonly string[];
  allowedMspIds: readonly string[];
  distributors: readonly { id: string | number; name: string }[];
  msps: readonly {
    id: string | number;
    name: string;
    distributor?: string | number | null;
    distributor_name?: string | null;
  }[];
  companies: readonly {
    id: string | number;
    name: string;
    msp: string | number;
    msp_name?: string | null;
  }[];
  /** The lists are still on their way. */
  loading: boolean;
  /** One of the lists failed, so an id missing from it may still be real. */
  incomplete: boolean;
}

export type OrgResolution =
  | { kind: "selection"; selection: ViewContext }
  | { kind: "refused"; reason: FallbackReason }
  | { kind: "pending" };

const same = (a: string | number | null | undefined, b: string) =>
  a != null && String(a).toLowerCase() === b;

const allowed = (ids: readonly string[], id: string) =>
  ids.length === 0 || ids.some((a) => same(a, id));

const NOTHING_BELOW = {
  mspId: null,
  mspName: null,
  companyId: null,
  companyName: null,
};

/**
 * Read an address's organization into the selection the bar would hold for it.
 *
 * The path above the organization is filled in from the lists, so the bar shows
 * the whole trail and its dropdowns offer the organization's siblings. A
 * selection left holding the previous distributor under a new company would
 * narrow the MSP dropdown to the wrong branch.
 */
export function resolveOrgParam(
  param: NamedOrgParam,
  reach: OrgReach,
): OrgResolution {
  if (param.kind === "malformed") return { kind: "refused", reason: "malformed" };
  if (param.kind === "default") {
    return {
      kind: "selection",
      selection: {
        scope: "default",
        distributorId: null,
        distributorName: null,
        ...NOTHING_BELOW,
      },
    };
  }
  if (param.kind === "all") {
    // The server refuses `all` below platform staff, and every request after
    // it would fail; refused here so the page never sends it.
    if (!reach.isPlatformStaff) return { kind: "refused", reason: "role" };
    return {
      kind: "selection",
      selection: {
        scope: "all",
        distributorId: null,
        distributorName: null,
        ...NOTHING_BELOW,
      },
    };
  }

  const id = param.id;
  const distributorOf = (msp: OrgReach["msps"][number] | undefined) => {
    if (msp?.distributor == null || msp.distributor === "") {
      return { distributorId: null, distributorName: null };
    }
    const distributorId = String(msp.distributor);
    const named = reach.distributors.find((d) => same(d.id, distributorId.toLowerCase()));
    return {
      distributorId,
      distributorName: msp.distributor_name ?? named?.name ?? null,
    };
  };

  const distributor = reach.distributors.find((d) => same(d.id, id));
  if (distributor && allowed(reach.allowedDistributorIds, id)) {
    return {
      kind: "selection",
      selection: {
        scope: "distributor",
        distributorId: String(distributor.id),
        distributorName: distributor.name,
        ...NOTHING_BELOW,
      },
    };
  }

  const msp = reach.msps.find((m) => same(m.id, id));
  if (msp && allowed(reach.allowedMspIds, id)) {
    return {
      kind: "selection",
      selection: {
        scope: "msp",
        ...distributorOf(msp),
        mspId: String(msp.id),
        mspName: msp.name,
        companyId: null,
        companyName: null,
      },
    };
  }

  const company = reach.companies.find((c) => same(c.id, id));
  if (company) {
    const parent = reach.msps.find((m) => same(m.id, String(company.msp).toLowerCase()));
    return {
      kind: "selection",
      selection: {
        scope: "company",
        ...distributorOf(parent),
        mspId: String(company.msp),
        mspName: company.msp_name ?? parent?.name ?? null,
        companyId: String(company.id),
        companyName: company.name,
      },
    };
  }

  if (reach.loading) return { kind: "pending" };
  // Not found in a list that failed to load is "could not check", not "you
  // may not": saying the second would be a claim nobody made.
  if (reach.incomplete) return { kind: "refused", reason: "unverified" };
  // Unknown and outside the caller's branch read the same on purpose: telling
  // them apart would say whether an organization the caller cannot see exists.
  return { kind: "refused", reason: "unknown" };
}

/**
 * `href` with `?org=` set to `token`, every other param and the hash kept.
 *
 * For links the shell builds (the sidebar, the command palette), so opening one
 * in a new tab carries the selection rather than whatever the store last held.
 */
export function withOrgParam(href: string, token: string): string {
  const hashAt = href.indexOf("#");
  const hash = hashAt >= 0 ? href.slice(hashAt) : "";
  const rest = hashAt >= 0 ? href.slice(0, hashAt) : href;
  const queryAt = rest.indexOf("?");
  const path = queryAt >= 0 ? rest.slice(0, queryAt) : rest;
  const params = new URLSearchParams(queryAt >= 0 ? rest.slice(queryAt + 1) : "");
  params.set(ORG_PARAM, token);
  return `${path}?${params.toString()}${hash}`;
}

// --- Falling back ------------------------------------------------------------

const FALLBACK_DESCRIPTION: Record<FallbackReason, string> = {
  malformed: "The organization in the address isn't a valid one.",
  unknown: "The organization in the address isn't one you can view.",
  role: "The whole-platform view is only available to platform staff.",
  unverified:
    "Your organizations couldn't be loaded, so the one in the address couldn't be checked.",
  refused: "The server refused the organization this page was narrowed to.",
};

/**
 * Say, briefly, that the page is showing the caller's default view and why.
 *
 * One toast id, so a burst of refusals (every request on a page fails the same
 * way) reads as one notice rather than a stack of them.
 */
export function noticeScopeFallback(reason: FallbackReason): void {
  toast.warning("Showing your default view", {
    id: "scope-fallback",
    description: FALLBACK_DESCRIPTION[reason],
  });
}

/**
 * A store change that corrects the selection rather than moving it.
 *
 * The address follows the store either way; the difference is the history. A
 * reader's own pick is pushed, so Back returns to what they had. A correction
 * (a refused or vanished organization) replaces the entry, or Back would lead
 * straight back into the refusal. Keyed by the token the correction produced,
 * so a marker nobody consumed cannot turn a later pick into a replace.
 *
 * `notice` is the reason to give when the address takes the correction. It is
 * left to the address rather than said at once for a correction that may not
 * stand: a vanished saved selection is reset as the lists land, and if the
 * address names a real organization in the same moment, that one is shown and
 * there is nothing to explain.
 */
export interface ScopeCorrection {
  notice: FallbackReason | null;
}

let correction: (ScopeCorrection & { token: string }) | null = null;

export function markScopeCorrection(
  token: string,
  notice: FallbackReason | null = null,
): void {
  correction = { token, notice };
}

/** The correction that moved the store to `token`, if it was one. Consumes the marker. */
export function takeScopeCorrection(token: string): ScopeCorrection | null {
  const hit =
    correction !== null && correction.token === token
      ? { notice: correction.notice }
      : null;
  correction = null;
  return hit;
}

/** Drop to the caller's default scope, as a correction, and say why. */
export function fallBackToDefault(reason: FallbackReason): void {
  markScopeCorrection(DEFAULT_TOKEN);
  useContextStore.getState().clearContext();
  noticeScopeFallback(reason);
}

// --- The server's refusal ----------------------------------------------------

/** The `X-Context-*` pair a request was stamped with from the store. */
export interface SentScope {
  scope: string;
  id: string | null;
}

/** Python's `str.capitalize()`, which is how the resolver names the level. */
function capitalized(scope: string): string {
  return scope.charAt(0).toUpperCase() + scope.slice(1).toLowerCase();
}

/** The code the server's scope resolver answers a refused selection with. */
export const SCOPE_REFUSED_CODE = "scope_refused";

/**
 * Whether a 400 or 403 is the server refusing the scope it was sent.
 *
 * The scope resolver answers with its own code, `scope_refused`, and that is
 * the signal. A backend from before the code answered a plain validation or
 * permission error, so for one of those the sentence is matched in full
 * against the scope and id the request carried, so that a refusal of
 * something else in similar words (a body field naming an MSP) is not
 * mistaken for one.
 */
export function isScopeRefusal(
  status: number,
  message: string | undefined,
  sent: SentScope,
  code?: string,
): boolean {
  if (status !== 400 && status !== 403) return false;
  if (code === SCOPE_REFUSED_CODE) return true;
  if (!message) return false;
  const level = capitalized(sent.scope);
  if (status === 400) {
    if (message.startsWith(`Unknown scope '${sent.scope}'.`)) return true;
    return sent.id != null && message === `${level} '${sent.id}' not found.`;
  }
  return (
    message === `Scope '${sent.scope}' is not permitted for your role.` ||
    message === `${level} is not within your assigned scope.` ||
    message === `${level} is not within your organization.`
  );
}

/**
 * The server refused the selection a request was stamped with.
 *
 * Every later request would be refused the same way, so the selection is
 * dropped to the caller's default. Only if the store still holds it: a burst of
 * refusals arrives after the first has already corrected things, and a reader
 * who moved on in between keeps where they went.
 */
export function refuseScopeFromServer(sent: SentScope): void {
  const held = tokenOf(useContextStore.getState());
  const refused = sent.id
    ? sent.id.toLowerCase()
    : sent.scope === "all"
      ? ALL_TOKEN
      : DEFAULT_TOKEN;
  if (held !== refused || held === DEFAULT_TOKEN) return;
  fallBackToDefault("refused");
}
